import { IconButton, ListItem, ListItemText, Skeleton } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DeleteIcon } from '../../icons';

export type UiFile = {
  name: string;
  size: number;
};

export type FileListItemProps = {
  file: UiFile;
  error?: string | undefined;
  loading?: boolean;
  onRemove: (file: UiFile) => void;
};

export function FileListItem({ file, error, loading, onRemove }: FileListItemProps) {
  const { i18n } = useTranslation();
  const fileSize = useMemo(() => {
    const formatter = Intl.NumberFormat(i18n.language, {
      unit: 'byte',
      style: 'unit',
      notation: 'compact',
      unitDisplay: 'narrow',
    });

    return formatter.format(file.size);
  }, [i18n.language, file.size]);

  const handleRemove = useCallback(() => {
    onRemove(file);
  }, [onRemove, file]);

  return (
    <ListItem
      secondaryAction={
        <IconButton aria-label="delete" onClick={handleRemove}>
          <DeleteIcon />
        </IconButton>
      }
    >
      <ListItemText
        primary={loading ? <Skeleton width={64} /> : file.name}
        secondary={loading ? <Skeleton width={32} /> : (error ?? fileSize)}
        slotProps={{
          primary: { color: 'text.primary' },
          secondary: { color: error ? 'error' : 'text.secondary' },
        }}
      />
    </ListItem>
  );
}
